import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { createCharacterAxie, type PlayableCharacter } from '../render/AxieCharacter.ts';

export function CharacterPreview({ character }: { character: PlayableCharacter }) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(host.clientWidth, host.clientHeight, false);
    host.replaceChildren(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, host.clientWidth / host.clientHeight, 0.1, 100);
    camera.position.set(0, 1.25, 3.8);
    camera.lookAt(0, 0.55, 0);
    scene.add(new THREE.HemisphereLight(0x8be9fd, 0x17102f, 2.2));
    const key = new THREE.DirectionalLight(0xffc65c, 2.1);
    key.position.set(2, 3, 4);
    scene.add(key);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.018, 8, 48), new THREE.MeshBasicMaterial({ color: 0x8be9fd, transparent: true, opacity: 0.55 }));
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.02;
    scene.add(ring);
    const abort = new AbortController();
    let disposed = false;
    let characterModel: Awaited<ReturnType<typeof createCharacterAxie>> | null = null;
    let raf = 0;
    let last = performance.now();
    const frame = (now: number): void => {
      raf = requestAnimationFrame(frame);
      const delta = Math.min((now - last) / 1000, 0.05);
      last = now;
      characterModel?.update(delta);
      ring.rotation.z += delta * 0.4;
      renderer.render(scene, camera);
    };
    void createCharacterAxie(character, renderer, abort.signal).then((model) => {
      if (disposed) { model.dispose(); return; }
      characterModel = model;
      model.object3D.position.y = 0.02;
      model.setFacing(1);
      scene.add(model.object3D);
    });
    const resize = (): void => {
      const width = host.clientWidth;
      const height = host.clientHeight;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    raf = requestAnimationFrame(frame);
    return () => {
      disposed = true;
      abort.abort();
      cancelAnimationFrame(raf);
      observer.disconnect();
      characterModel?.dispose();
      ring.geometry.dispose();
      (ring.material as THREE.Material).dispose();
      renderer.dispose();
      host.replaceChildren();
    };
  }, [character]);

  return <div ref={hostRef} className="character-preview" aria-label={`${character.name} character preview`} />;
}
